// dependencies
import React, { useState, useCallback } from 'react'
import debounce from 'lodash.debounce'

// css
import '../../css/pages/PartnerBrandedAudiences.styl'

// components
import { Button, Input, ListView, Select, Switch } from '@st-operations/uikit/src/v2/index.js'
import Header from '_components/header/index.jsx'

// functions
import searchEntities from '../lib/searchEntities'

const debouncedSearchEntities = debounce(searchEntities, 300)

const BrandedAudienceSelector = (props) => {
  const [state, setState] = useState({
    options: [],
    optionsMap: {},
    value: '',
  })

  const search = async (query) => {
    const qs = {
      query,
      kind: 'taxonomy',
      is_branded: true,
      limit: 10,
    }
    debouncedSearchEntities('legacy-audiences', qs, (response) => {
      const options = response.items.map((item) => {
        return `${item?.taxonomy?.name} > ${item?.name}`
      })
      const optionsMap = response.items.reduce((acc, item) => {
        return { ...acc, [`${item?.taxonomy?.name} > ${item?.name}`]: item }
      }, {})
      setState((prevState) => ({
        ...prevState,
        options,
        optionsMap,
      }))
    })
  }

  const onChange = useCallback((value) => {
    setState((prevState) => ({ ...prevState, value }))
    if (value) {
      search(value)
    }
  }, [])

  const onOptionsClick = (value) => {
    let audience = state.optionsMap[value]
    props.onSelect(audience)
  }

  return (
    <div className="branded-audience-selector">
      <Select
        label="Search for a branded audience"
        onChange={onChange}
        onOptionClick={onOptionsClick}
        options={state.options}
        placeholder="search for a branded audience"
        value={state.value}
        usePortal
      />
    </div>
  )
}

const BrandedAudienceForm = (props) => {
  const initial_state = {
    appnexus_cpm_rate: '0.95',
    appnexus_global_name: '',
    appnexus_us_name: '',
    buyable: true,
    global_id: '',
    sync_appnexus_global: true,
    sync_appnexus_us: true,
    sync_ttd_global: true,
    sync_ttd_us: true,
    ttd_name: '',
    ttd_parent_global_id: '',
    ttd_parent_us_id: '',
    ttd_should_inherit_rate: true,
    us_id: '',
  }

  const [state, setState] = useState(initial_state)

  const onSelect = (audience) => {
    const name_parts = audience?.name?.split('>')
    const ttd_name = name_parts[name_parts.length - 1].trim()
    setState((prevState) => ({
      ...prevState,
      appnexus_global_name: `ShareThis > ${audience.name}`,
      appnexus_us_name: `ShareThis_US > ${audience.name}`,
      audience: { _id: audience._id },
      global_id: audience?.ids?.global || '',
      name: audience.name || '',
      ttd_name,
      us_id: audience?.ids?.us || '',
    }))
  }

  const onCreate = () => {
    window._api.post(`/partner-branded-audiences`, state, (error, item) => {
      if (error) {
        toast.error(error.message)
      } else {
        toast('Creation successful!')
        setState(initial_state)
        if (props.onCreate) {
          props.onCreate()
        }
      }
    })
  }

  return (
    <div className="partner-branded-audiences-left-column">
      <BrandedAudienceSelector onSelect={onSelect} />
      <div className="partner-branded-audiences-left-column-inputs">
        <Input
          label="Global ID"
          name="global_id"
          onChange={(global_id) => setState((prevState) => ({ ...prevState, global_id }))}
          value={state?.global_id}
        />
        <Input
          label="US ID"
          name="us_id"
          onChange={(us_id) => setState((prevState) => ({ ...prevState, us_id }))}
          value={state?.us_id}
        />
      </div>
      <div className="switches">
        <span>Buyable</span>
        <Switch
          checked={state?.buyable}
          onChange={() => setState((prevState) => ({ ...prevState, buyable: !prevState.buyable }))}
        />
      </div>
      <div className="switches">
        <div className="switches-title">Sync With TTD</div>
        <span>Global</span>
        <Switch
          checked={state?.sync_ttd_global}
          onChange={() =>
            setState((prevState) => ({
              ...prevState,
              sync_ttd_global: !prevState.sync_ttd_global,
            }))
          }
        />
        <span>US</span>
        <Switch
          checked={state?.sync_ttd_us}
          onChange={() =>
            setState((prevState) => ({
              ...prevState,
              sync_ttd_us: !prevState.sync_ttd_us,
            }))
          }
        />
      </div>
      <Input
        label="TTD Name"
        name="ttd_name"
        onChange={(ttd_name) => setState((prevState) => ({ ...prevState, ttd_name }))}
        value={state?.ttd_name}
      />
      <div className="partner-branded-audiences-left-column-inputs">
        <Input
          label="TTD Parent Global ID"
          name="ttd_parent_global_id"
          onChange={(ttd_parent_global_id) =>
            setState((prevState) => ({ ...prevState, ttd_parent_global_id }))
          }
          value={state?.ttd_parent_global_id}
        />
        <Input
          label="TTD Parent US ID"
          name="ttd_parent_us_id"
          onChange={(ttd_parent_us_id) =>
            setState((prevState) => ({ ...prevState, ttd_parent_us_id }))
          }
          value={state?.ttd_parent_us_id}
        />
      </div>
      <div className="switches">
        <div className="switches-title">Sync With AppNexus</div>
        <span>Global</span>
        <Switch
          checked={state?.sync_appnexus_global}
          onChange={() =>
            setState((prevState) => ({
              ...prevState,
              sync_appnexus_global: !prevState.sync_appnexus_global,
            }))
          }
        />
        <span>US</span>
        <Switch
          checked={state?.sync_appnexus_us}
          onChange={() =>
            setState((prevState) => ({
              ...prevState,
              sync_appnexus_us: !prevState.sync_appnexus_us,
            }))
          }
        />
      </div>
      <Input
        label="AppNexus Global Name"
        name="appnexus_global_name"
        onChange={(appnexus_global_name) =>
          setState((prevState) => ({ ...prevState, appnexus_global_name }))
        }
        value={state?.appnexus_global_name}
      />
      <Input
        label="AppNexus US Name"
        name="appnexus_us_name"
        onChange={(appnexus_us_name) =>
          setState((prevState) => ({ ...prevState, appnexus_us_name }))
        }
        value={state?.appnexus_us_name}
      />
      <Input
        label="AppNexus CPM"
        name="appnexus_cpm_rate"
        onChange={(appnexus_cpm_rate) =>
          setState((prevState) => ({ ...prevState, appnexus_cpm_rate }))
        }
        value={state.appnexus_cpm_rate}
      />
      <Button onClick={onCreate}>Create</Button>
    </div>
  )
}

// Partner Branded Audiences Page
const PartnerBrandedAudiences = () => {
  const onCreate = () => {
    // this is a hack
    // a better solution is to force the listview to query new data
    window.location.reload()
  }

  return (
    <div id="partner-branded-audiences-page">
      <Header title="Partner Branded Audiences" />
      <div className="partner-branded-audiences-grid">
        <BrandedAudienceForm onCreate={onCreate} />
        <div className="partner-branded-audiences-right-column">
          <ListView
            columns={[
              {
                name: 'Name',
                key: 'name',
                link: `/partner-branded-audiences/:_id`,
                sortable: true,
              },
              {
                key: 'created_at',
                link: `/partner-branded-audiences/:id`,
                name: 'Created At',
              },
            ]}
            entities="partner-branded-audiences"
            fields={['name', 'created_at']}
            searchable={true}
            sort_dir={1}
            sort_key={'name'}
          />
        </div>
      </div>
    </div>
  )
}

// export
export default PartnerBrandedAudiences
